/**
 * Video Player Composable
 * Provides reusable video player functionality for components
 * Handles video state management, controls, and lifecycle events
 */
import { ref, nextTick } from 'vue';

// MediaError.code values, per the HTML spec
const MEDIA_ERROR_REASONS = {
    1: 'aborted',
    2: 'network',
    3: 'decode',
    4: 'src-not-supported',
};

export function useVideoPlayer() {
    const videoPausedStates = ref({});
    // Separate reactive state for audio - video.muted is not reactive in Vue
    // This allows the UI to update when mute state changes
    const videoAudioStates = ref({});
    const videoAutoplayBlocked = ref({});
    // Tracks videos whose source failed to load, so the UI can replace the dead
    // player with an explanation instead of leaving a blank frame.
    const videoErrorStates = ref({});

    /**
     * Gets a video element by its ref index
     * Handles array refs returned by Vue
     * Internal helper — callers use the behavioural methods below
     *
     * @param {Object} refs - Component refs object
     * @param {number} index - Index of the video ref
     * @returns {HTMLVideoElement|undefined} Video element or undefined
     */
    const getVideoElement = (refs, index) => {
        const videoRef = refs[`video-${index}`];
        return Array.isArray(videoRef) ? videoRef[0] : videoRef;
    };

    /**
     * Toggles video playback between play and pause
     *
     * @param {Object} refs - Component refs object
     * @param {number} index - Index of the video to toggle
     */
    const toggleVideoPlayback = (refs, index) => {
        const video = getVideoElement(refs, index);

        if (video) {
            if (video.paused) {
                video.play();
                videoPausedStates.value = { ...videoPausedStates.value, [index]: false };
            } else {
                video.pause();
                videoPausedStates.value = { ...videoPausedStates.value, [index]: true };
            }
        }
    };

    /**
     * Toggles video audio mute state
     *
     * @param {Object} refs - Component refs object
     * @param {number} index - Index of the video to toggle
     */
    const toggleVideoMute = (refs, index) => {
        const video = getVideoElement(refs, index);

        if (video) {
            video.muted = !video.muted;
            videoAudioStates.value = { ...videoAudioStates.value, [index]: !video.muted };
        }
    };

    /**
     * Pauses all videos in the collection and optionally resets them
     *
     * @param {Object} refs - Component refs object
     * @param {Array} slides - Array of slide objects containing videos
     * @param {boolean} resetTime - Whether to reset video to beginning (default: true)
     */
    const pauseAllVideos = (refs, slides, resetTime = true) => {
        slides.forEach((_, slideIndex) => {
            const video = getVideoElement(refs, slideIndex);
            if (video && !video.paused) {
                video.pause();
                if (resetTime) {
                    video.currentTime = 0;
                }
            }
        });
    };

    /**
     * Plays a specific video by index
     *
     * @param {Object} refs - Component refs object
     * @param {number} index - Index of the video to play
     * @param {boolean} resetTime - Whether to reset to beginning (default: true)
     */
    const playVideo = (refs, index, resetTime = true) => {
        nextTick(() => {
            const video = getVideoElement(refs, index);
            if (video) {
                if (resetTime) {
                    video.currentTime = 0;
                }
                video.play().catch((error) => {
                    console.warn('Autoplay was prevented by the browser. User interaction is required to start the video.', error);
                    // Update state to show video is paused and autoplay was blocked
                    videoPausedStates.value = { ...videoPausedStates.value, [index]: true };
                    videoAutoplayBlocked.value = { ...videoAutoplayBlocked.value, [index]: true };
                });
            }
        });
    };

    /**
     * Video lifecycle event handlers
     * These can be overridden in components for custom behavior
     */

    /**
     * Called when video is loaded and ready to play
     *
     * @param {Object} refs - Component refs object
     * @param {number} index - Index of the loaded video
     * @param {number} currentIndex - Currently active slide index (optional)
     */
    const onVideoLoaded = (refs, index, currentIndex = null) => {
        const video = getVideoElement(refs, index);

        if (video) {
            // A source that loads clears any previous failure, so a refetched URL recovers
            videoErrorStates.value = { ...videoErrorStates.value, [index]: false };

            // Initialize audio state (start with audio enabled)
            video.muted = false;
            videoAudioStates.value = { ...videoAudioStates.value, [index]: true };

            // Only autoplay if this is the currently visible slide
            if (currentIndex === null || index === currentIndex) {
                video.play().catch((error) => {
                    // Autoplay prevented by browser
                    console.warn('Autoplay was prevented by the browser. User interaction is required to start the video.', error);
                    videoPausedStates.value = { ...videoPausedStates.value, [index]: true };
                    videoAutoplayBlocked.value = { ...videoAutoplayBlocked.value, [index]: true };
                });
            }
        }
    };

    /**
     * Called when video starts playing
     * Updates UI state for play/pause button
     *
     * @param {number} index - Index of the playing video
     */
    const onVideoPlay = (index) => {
        videoPausedStates.value = { ...videoPausedStates.value, [index]: false };
    };

    /**
     * Called when video is paused
     * Updates UI state for play/pause button
     *
     * @param {number} index - Index of the paused video
     */
    const onVideoPause = (index) => {
        videoPausedStates.value = { ...videoPausedStates.value, [index]: true };
    };

    /**
     * Called when video playback reaches the end
     * Video player does not track completion automatically
     * Component can track this event if needed
     *
     * @param {number} index - Index of the completed video
     */
    const onVideoEnded = (index) => {
        videoPausedStates.value = { ...videoPausedStates.value, [index]: true };
    };

    /**
     * Called when video encounters an error
     *
     * @param {number} index - Index of the video with error
     * @param {Event} event - Error event object
     */
    const onVideoError = (index, event) => {
        // Log the media error code and resolved URL, not the bare event — the event
        // alone doesn't say whether this was a network failure, a decode failure, or
        // an unsupported/dead source, which is what actually needs diagnosing.
        const video = event?.target;
        const { code, message } = video?.error || {};

        videoErrorStates.value = { ...videoErrorStates.value, [index]: true };

        console.error(`Video ${index} failed to load:`, {
            code,
            reason: MEDIA_ERROR_REASONS[code] || 'unknown',
            message,
            src: video?.currentSrc || video?.src,
        });
    };

    return {
        // State
        videoPausedStates,
        videoAudioStates,
        videoAutoplayBlocked,
        videoErrorStates,

        // Methods
        toggleVideoPlayback,
        toggleVideoMute,
        pauseAllVideos,
        playVideo,

        // Event handlers
        onVideoLoaded,
        onVideoPlay,
        onVideoPause,
        onVideoEnded,
        onVideoError,
    };
}
